import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { Select } from "../../../design/select";
import { Textarea } from "../../../design/textarea";
import { Text } from "../../../design/text";
import { FinancialItem } from "../../../models/FinancialItem";
import { FinancialItemForm } from "../../../models/Forms/FinancialItemForm";
import { DialogActions, DialogBody, DialogTitle } from "../../../design/dialog";
import { Button } from "../../../design/button";
import SelectFinancialAccountsField from "../../../components/Fields/SelectFinancialAccountsField";
import { useFinancialAccount } from "../../../contexts/FinancialAccountContext";

interface Props {
  form: FinancialItemForm;
  handleUpdateForm: (form: FinancialItemForm) => void;
  errors: any;
  onBack: () => void;
  onSubmit: (form: FinancialItemForm) => void;
}

const FinancialItemFormComponent: React.FC<Props> = ({
  form,
  handleUpdateForm,
  errors,
  onBack,
  onSubmit,
}) => {
  const { financialAccounts } = useFinancialAccount();

  return (
    <>
      <DialogTitle>Create Financial Item</DialogTitle>
      <DialogBody>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Name</Label>
              <Input
                type="text"
                value={form.name}
                placeholder="Name"
                onChange={(e) =>
                  handleUpdateForm({ ...form, name: e.target.value })
                }
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>

            {/* <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Category</Label>
                <Select
                  value={form.category || ""}
                  onChange={(e) =>
                    handleUpdateForm({
                      ...form,
                      category: e.target.value as FinancialItem["category"],
                    })
                  }
                >
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                </Select>
                {errors.category && (
                  <ErrorMessage>{errors.category}</ErrorMessage>
                )}
              </Field>
            </div> */}

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Amount</Label>
                <Input
                  type="number"
                  value={form.amount || ""}
                  onChange={(e) =>
                    handleUpdateForm({
                      ...form,
                      amount: Number(e.target.value),
                    })
                  }
                  placeholder="Amount"
                />
                {form.zillowPropertyValue && (
                  <Text className="text-sm text-gray-500">{`*Zillow Estimate: $${form.zillowPropertyValue.toLocaleString()}`}</Text>
                )}
                {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
              </Field>

              <Field>
                <Label>Associated Debt Amount</Label>
                <Input
                  type="number"
                  value={form.associatedDebtAmount || ""}
                  onChange={(e) =>
                    handleUpdateForm({
                      ...form,
                      associatedDebtAmount: Number(e.target.value),
                    })
                  }
                  placeholder="Associated Debt Amount"
                />
                {errors.associatedDebtAmount && (
                  <ErrorMessage>{errors.associatedDebtAmount}</ErrorMessage>
                )}
              </Field>
            </div>

            {form.type === "real_estate" && (
              <SelectFinancialAccountsField
                title="Associated Liabilities"
                values={form.associatedLiabilityIds ?? []}
                onChange={(values) => {
                  let associatedDebtAmount = financialAccounts
                    .filter((f) => values.includes(f._id))
                    .reduce((sum, item) => sum + (item.currentBalance ?? 0), 0);
                  handleUpdateForm({
                    ...form,
                    associatedLiabilityIds: values,
                    associatedDebtAmount,
                  });
                }}
              />
            )}

            <Field>
              <Label>Description</Label>
              <Textarea
                rows={5}
                placeholder="Description"
                value={form.description}
                onChange={(e) =>
                  handleUpdateForm({ ...form, description: e.target.value })
                }
              />
              {errors.description && (
                <ErrorMessage>{errors.description}</ErrorMessage>
              )}
            </Field>

            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Date Valued</Label>
                <Input
                  type="date"
                  value={
                    form.dateValued
                      ? form.dateValued.toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handleUpdateForm({
                      ...form,
                      dateValued: new Date(e.target.value),
                    })
                  }
                />
                {errors.dateValued && (
                  <ErrorMessage>{errors.dateValued}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Date Acquired</Label>
                <Input
                  type="date"
                  value={
                    form.dateAcquired
                      ? form.dateAcquired.toISOString().split("T")[0]
                      : ""
                  }
                  onChange={(e) =>
                    handleUpdateForm({
                      ...form,
                      dateAcquired: new Date(e.target.value),
                    })
                  }
                />
                {errors.dateAcquired && (
                  <ErrorMessage>{errors.dateAcquired}</ErrorMessage>
                )}
              </Field>
            </div>

            <Field>
              <Label>Ownership Status</Label>
              <Select
                value={form.ownershipStatus || ""}
                onChange={(e) => {
                  const status = e.target
                    .value as FinancialItem["ownershipStatus"];
                  handleUpdateForm({
                    ...form,
                    ownershipStatus: status,
                    ownershipPercentage: status === "marital" ? 50 : 100,
                  });
                }}
              >
                <option value="" disabled>
                  Select Ownership Status
                </option>
                <option value="separate">Separate</option>
                <option value="marital">Marital</option>
              </Select>
              {errors.ownershipStatus && (
                <ErrorMessage>{errors.ownershipStatus}</ErrorMessage>
              )}
            </Field>

            {form.ownershipStatus === "marital" && (
              <Field>
                <Label>Ownership Percentage</Label>
                <Input
                  type="number"
                  value={form.ownershipPercentage || ""}
                  onChange={(e) =>
                    handleUpdateForm({
                      ...form,
                      ownershipPercentage: Number(e.target.value),
                    })
                  }
                  placeholder="Ownership Percentage"
                />
                {errors.ownershipPercentage && (
                  <ErrorMessage>{errors.ownershipPercentage}</ErrorMessage>
                )}
              </Field>
            )}
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain type="button" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" onClick={onSubmit}>
          Submit
        </Button>
      </DialogActions>
    </>
  );
};

export default FinancialItemFormComponent;
